import { useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { useTranslation } from "@coworker/locales";
import {
  useStoreId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  useNotifications,
  useSetNotificationAsUnread,
  useSetAllNotificationsAsUnread,
} from "../hooks/useNotifications";
import { NotificationItem } from "./NotificationItem";
import { useSseNotifications } from "../hooks/useSseNotifications";
import styles from "./notification.module.css";
import { FixaModalHeader } from "../../shared/wrappers/FixaModal/FixaModalHeader";
import { NotificationsInboxIcon } from "./NotificationsInboxIcon";

export const NotificationsInbox = () => {
  const { t } = useTranslation();
  const storeId = useStoreId();
  const uid = useUID();
  const { data: notifications, error } = useNotifications(storeId, uid);
  const { mutateAsync: mutateNotificationAsUnread } =
    useSetNotificationAsUnread(uid);
  const [modalVisible, setModalVisible] = useState(false);
  const latestNotificationCreatedAt = useMemo(() => {
    if (notifications && notifications.length) {
      return notifications[0]!.createdAt;
    }
    return "";
  }, [notifications]);
  useSseNotifications(storeId, uid, latestNotificationCreatedAt);

  const unreadIdArray: string[] = useMemo(
    () =>
      notifications
        ? notifications.reduce((acc, curr) => {
            if (!curr.read) {
              acc.push(curr.id);
            }
            return acc;
          }, [] as string[])
        : [],
    [notifications]
  );
  const { mutateAsync: mutateAllNotificationsAsUnread } =
    useSetAllNotificationsAsUnread(uid);

  const onMarkAllRead = () => {
    if (unreadIdArray.length) {
      mutateAllNotificationsAsUnread(unreadIdArray);
    }
  };

  const inboxStyles = [styles["notification-inbox"] ?? ""];
  if (unreadIdArray.length) {
    inboxStyles.push(styles["notification-inbox-with-unread"] ?? "");
  }

  const unreadCount = unreadIdArray.length;
  const hasNotificationsData = notifications !== undefined;
  const inboxAriaLabel =
    unreadCount > 0
      ? `${t("notificationsString")} (${unreadCount})`
      : t("notificationsString");

  return (
    <div className={inboxStyles.join(" ")}>
      <NotificationsInboxIcon
        unreadCount={unreadCount}
        hasNotificationsData={hasNotificationsData}
        ariaLabel={inboxAriaLabel}
        onClick={() => setModalVisible(!modalVisible)}
      />
      <FixaModal
        visible={modalVisible}
        handleCloseBtn={() => {
          setModalVisible(false);
        }}
      >
        <FixaSheets
          header={<FixaModalHeader ariaCloseTxt={t("closeActionString")} />}
          footer={null}
          fullSize={true}
        >
          <FixaModalBody className={styles["notification-inbox-body"] ?? ""}>
            <div className={styles["notification-inbox-header"] ?? ""}>
              <div>
                <span>{t("notificationsString")}</span>
              </div>
              <FixaButton
                aria-label={t("markAllReadWithCountString", {
                  count: unreadIdArray.length,
                })}
                data-testid={"mark all read button"}
                text={t("markAllReadWithCountString", {
                  count: unreadIdArray.length,
                })}
                type="tertiary"
                inverseTheme
                onClick={() => onMarkAllRead()}
              />
            </div>
            {notifications !== undefined &&
              notifications?.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={(notificationId) => {
                    setModalVisible(false);
                    mutateNotificationAsUnread(notificationId);
                  }}
                />
              ))}
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </div>
  );
};
